import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import type { SupportTask, TaskPriority, TaskStatus, TaskType } from '../../../common/models/support.model';
import { TASK_PRIORITIES, TASK_STATUSES, TASK_TYPES } from '../../../common/models/support.model';
import type { TaskPayload } from '../../../common/hooks/use-support-api.hook';
import { TASK_PRIORITY_META, TASK_STATUS_META, TASK_TYPE_META } from '../support-meta';
import { PriorityBars } from '../components/TaskBadge';
import { AssigneePicker } from './StaffPeople';

export interface TaskDraft {
  type: TaskType;
  title: string;
  description: string;
  status: TaskStatus;
  priority: TaskPriority;
  assignee: string;
  labels: string;
  visibleToVendor: boolean;
}

export const draftFromTask = (t?: Partial<SupportTask>): TaskDraft => ({
  type: t?.type || 'bug',
  title: t?.title || '',
  description: t?.description || '',
  status: t?.status || 'todo',
  priority: t?.priority || 'medium',
  assignee: t?.assignee || '',
  labels: (t?.labels || []).join(', '),
  visibleToVendor: t?.visibleToVendor ?? true,
});

export const payloadFromDraft = (d: TaskDraft): TaskPayload => ({
  type: d.type,
  title: d.title.trim(),
  description: d.description,
  status: d.status,
  priority: d.priority,
  // '' clears it; the API keeps '' as unassigned.
  assignee: d.assignee,
  labels: d.labels.split(',').map((l) => l.trim()).filter(Boolean).slice(0, 10),
  visibleToVendor: d.visibleToVendor,
});

/** Type, title, details, and how it's tracked. Shared by the inbox ("raise a task") and the board. */
export function TaskFields({ draft, onChange, linkedToVendor, me }: { draft: TaskDraft; onChange: (d: TaskDraft) => void; linkedToVendor?: boolean; me?: string }) {
  const { t } = useTranslation();
  const set = <K extends keyof TaskDraft>(k: K, v: TaskDraft[K]) => onChange({ ...draft, [k]: v });

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-4 gap-2" role="radiogroup" aria-label={t('SUPPORT.TASK.TYPE')}>
        {TASK_TYPES.map((type) => {
          const meta = TASK_TYPE_META[type];
          const Icon = meta.icon;
          const active = draft.type === type;
          return (
            <button
              key={type}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => set('type', type)}
              className={cn(
                'flex flex-col items-center gap-1.5 rounded-xl border p-2.5 text-xs transition-colors',
                active ? 'border-foreground ring-[0.5px] ring-foreground' : 'text-muted-foreground hover:bg-muted/60',
              )}
            >
              <Icon className={cn('size-4', active && meta.tone)} />
              {t(meta.labelKey)}
            </button>
          );
        })}
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="task-title" className="text-xs text-muted-foreground">{t('SUPPORT.TASK.TITLE')}</Label>
        <Input id="task-title" value={draft.title} maxLength={200} onChange={(e) => set('title', e.target.value)} autoFocus />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="task-description" className="text-xs text-muted-foreground">{t('SUPPORT.TASK.DESCRIPTION')}</Label>
        <Textarea id="task-description" value={draft.description} rows={5} onChange={(e) => set('description', e.target.value)} placeholder={t('SUPPORT.TASK.DESCRIPTION_PLACEHOLDER')} />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs text-muted-foreground">{t('SUPPORT.TASK.STATUS')}</Label>
          <Select value={draft.status} onValueChange={(v) => set('status', v as TaskStatus)}>
            <SelectTrigger className="w-full" aria-label={t('SUPPORT.TASK.STATUS')}><SelectValue /></SelectTrigger>
            <SelectContent>
              {TASK_STATUSES.map((s) => (
                <SelectItem key={s} value={s}>
                  <span className={cn('size-1.5 rounded-full', TASK_STATUS_META[s].dot)} />
                  {t(TASK_STATUS_META[s].labelKey)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs text-muted-foreground">{t('SUPPORT.TASK.PRIORITY')}</Label>
          <Select value={draft.priority} onValueChange={(v) => set('priority', v as TaskPriority)}>
            <SelectTrigger className="w-full" aria-label={t('SUPPORT.TASK.PRIORITY')}><SelectValue /></SelectTrigger>
            <SelectContent>
              {TASK_PRIORITIES.map((p) => (
                <SelectItem key={p} value={p}>
                  <PriorityBars bars={TASK_PRIORITY_META[p].bars} tone={TASK_PRIORITY_META[p].tone} />
                  {t(TASK_PRIORITY_META[p].labelKey)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="task-assignee" className="text-xs text-muted-foreground">{t('SUPPORT.TASK.ASSIGNEE')}</Label>
          <AssigneePicker id="task-assignee" className="w-full" value={draft.assignee} me={me} onChange={(v) => set('assignee', v)} />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="task-labels" className="text-xs text-muted-foreground">{t('SUPPORT.TASK.LABELS')}</Label>
          <Input id="task-labels" value={draft.labels} onChange={(e) => set('labels', e.target.value)} placeholder={t('SUPPORT.TASK.LABELS_PLACEHOLDER')} />
        </div>
      </div>

      <label className="flex items-start justify-between gap-4 rounded-xl border p-3">
        <span className="flex flex-col gap-0.5">
          <span className="text-sm font-medium">{t('SUPPORT.TASK.VISIBLE')}</span>
          <span className="text-xs text-muted-foreground">
            {linkedToVendor ? t('SUPPORT.TASK.VISIBLE_HINT') : t('SUPPORT.TASK.VISIBLE_HINT_UNLINKED')}
          </span>
        </span>
        <Switch checked={draft.visibleToVendor} onCheckedChange={(v) => set('visibleToVendor', v)} aria-label={t('SUPPORT.TASK.VISIBLE')} />
      </label>
    </div>
  );
}

/** "Raise a task" / "New task": the fields in a dialog. */
export function TaskDialog({
  open,
  onOpenChange,
  initial,
  title,
  description,
  linkedToVendor,
  me,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  initial: TaskDraft;
  title: string;
  description?: string;
  linkedToVendor?: boolean;
  me?: string;
  onSubmit: (payload: TaskPayload) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setDraft(initial);
      setError(null);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    if (!draft.title.trim()) {
      setError(t('SUPPORT.TASK.TITLE_REQUIRED'));
      return;
    }
    setBusy(true);
    try {
      await onSubmit(payloadFromDraft(draft));
      onOpenChange(false);
    } catch (e: any) {
      setError(e?.message || t('SUPPORT.TASK.SAVE_FAILED'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <TaskFields draft={draft} onChange={setDraft} linkedToVendor={linkedToVendor} me={me} />
        {error && <p className="text-sm text-destructive">{error}</p>}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>{t('SUPPORT.ACTIONS.CANCEL')}</Button>
          <Button variant="outline" className="border-foreground" onClick={submit} disabled={busy}>
            {busy && <Spinner data-icon="inline-start" />}
            {t('SUPPORT.TASK.CREATE')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
